import { queryOptions } from '@tanstack/react-query';
import { useCurrentUser } from '@/hooks/queries/auth.ts';
import { api } from '@/lib/api.ts';
import type { APIParkadminListPark, APIParkData } from '@/types/index.ts';

/**
 * The parks the current user manages (`GET parkadmin/current/parks/`), used to
 * populate the sidebar park selector and as the home dashboard's cross-park
 * source (map coordinates, charging-point counts) — one fetch, many consumers.
 * Keyed by user so the list is refetched when the logged-in (or impersonated)
 * user changes.
 *
 * Sorted alphabetically by displayed name (`label || name`) here so every
 * consumer sees a stable order without re-sorting.
 */
export function useParksOptions() {
  const user = useCurrentUser();
  return queryOptions({
    queryKey: ['user', user.id.toString(), 'park'],
    queryFn: async () => {
      // "trust the schema bro"
      const { data } = await api.get<Array<APIParkadminListPark>>('/parkadmin/current/parks/');
      return [...data].sort((a, b) => (a.label || a.name).localeCompare(b.label || b.name));
    },
  });
}

/**
 * Full data for a single park (`GET parkadmin/current/parks/{id}/`), shared by
 * the park dashboard and every section card that reads `park_data` — including
 * the charge points card, which reads the embedded `chargingstations[]` rather
 * than a separate request (the standalone `/chargingstations/` endpoint returns
 * the same schema, so the embedded array is the single source of truth). The park
 * id is a route param (`/park/$parkId`), so it is always a definite number here:
 * the dashboard route's loader prefetches this via `ensureQueryData` and the
 * component reads it with `useSuspenseQuery`.
 *
 * `chargingstations` is sorted by `park_bnum` here so every consumer sees a stable
 * order without re-sorting.
 */
export function parkDataQueryOptions(parkId: number) {
  return queryOptions({
    queryKey: ['park', parkId, 'data'],
    queryFn: async () => {
      const { data } = await api.get<APIParkData>(`parkadmin/current/parks/${parkId}/`);
      return {
        ...data,
        chargingstations: [...data.chargingstations].sort((a, b) => a.park_bnum - b.park_bnum),
      };
    },
  });
}
